import { Formik } from 'formik';
import React, { Fragment } from 'react';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import { notificationStore } from '../../../components/layout/notifications/NotificationStore';
import { Button } from '../../../components/layout/button/Button';
import { candidatesStore } from '../CandidatesStore';
import { JobRoleDropdown } from '../../../components/layout/form-fields/inputs/JobRoleDropdown';
import { MultiDropdown } from '../../../components/layout/form-fields/inputs/MultiDropdown';
import { SkillDropdown } from '../../../components/layout/form-fields/inputs/SkillDropdown';
import { TimezoneDropdown } from '../../../components/timezones/shared/TimezoneDropdown';
import { WorkLocationInput } from '../../../components/layout/form-fields/inputs/WorkLocationInput';
import { FieldRequiredIndicator } from '../../../components/layout/form-fields/inputs/FieldRequiredIndicator';
import { FormFieldError } from '../../../components/layout/form-fields/FormFieldError';
import DropdownField from '../../../components/layout/form-fields/DropdownField';
import { currencyStore } from '../../../components/settings/currencies/CurrencyStore';
import { FormField } from 'components/layout/form-fields/FormField';
import { Dropdown } from 'components/layout/form-fields/inputs/Dropdown';
import { modalStore } from 'components/layout/modals/ModalStore';
import { CandidateCompensationCreate } from 'components/candidates/profile/compensations/CandidateCompensationCreate';
import { candidateProfileStore } from '../CandidateProfileStore';
import { ConditionalContent } from 'components/layout/form-fields/inputs/ConditionalContent';
import { Loader } from 'components/layout/misc/Loader';

const compensationFieldValidation = (fieldLabel, showField) =>
	Yup.mixed()
		.label(fieldLabel)
		.when(showField, {
			is: true,
			then: schema =>
				schema
					.test(
						'is-special-required',
						`${fieldLabel} is required`,
						value => !!value
					)
					.required()
		});

const ValidationSchema = Yup.object().shape({
	preferredJobRoles: Yup.array()
		.min(1, 'Please select at least one Role')
		.required()
		.label('Roles'),
	preferredSkills: Yup.array()
		.min(1, 'Please select at least one Skill')
		.required()
		.label('Technologies'),
	preferredWorkLocation: Yup.string()
		.nullable()
		.required()
		.label('Work Location'),
	preferredPositionType: Yup.array()
		.min(1, 'Please select at least one Position Type')
		.required()
		.label('Position Types'),
	preferredWorkshifts: Yup.array()
		.min(1, 'Please select at least one Workshift')
		.required()
		.label('Workshifts'),
	preferredTimeZone: Yup.object().nullable().required().label('Time Zone'),
	showingCompensationFields: Yup.boolean(),
	currentCurrencyId: compensationFieldValidation(
		'Current Currency',
		'showingCompensationFields'
	),
	currentAmount: Yup.number()
		.label('Current Amount')
		.when('showingCompensationFields', {
			is: true,
			then: schema => schema.required()
		}),
	currentScheme: compensationFieldValidation(
		'Current Scheme',
		'showingCompensationFields'
	),
	expectedCurrencyId: compensationFieldValidation(
		'Expected Currency',
		'showingCompensationFields'
	),
	expectedAmount: Yup.number()
		.label('Expected Amount')
		.when('showingCompensationFields', {
			is: true,
			then: schema => schema.required()
		}),
	expectedScheme: compensationFieldValidation(
		'Expected Scheme',
		'showingCompensationFields'
	)
});

export class CandidateProfileExpectationsEdit extends React.Component {
	state = {
		saving: false,
		loaded: false,
		currencies: []
	};
	getValueFromOptions(value, options) {
		const option = options.find(option => {
			return option.value === value;
		});
		return option ? option.text : 'N/A';
	}
	isInitialValid(props) {
		if (!props.validationSchema) return true;
		return props.validationSchema.isValidSync(props.initialValues);
	}

	findCurrencyObject(currencyId) {
		return this.state.currencies.find(currency => currency.id === currencyId);
	}

	async componentDidMount() {
		const { data: currencies } = await currencyStore.getCurrencies('');

		this.setState({ currencies, loaded: true });
	}

	renderCompensationSection(
		formikBag,
		currencyMap,
		{ title, currencyField, amountField, schemeField }
	) {
		const schemeOptions = [
			{ text: 'Select Scheme', value: '' },
			{ text: 'Monthly', value: 'monthly' },
			{ text: 'Hourly', value: 'hourly' }
		];

		return (
			<div className="has-text-left">
				<label className="title is-5 has-text-weight-bold">{title}</label>
				<div className="columns">
					<p className="control column is-4">
						<div className="field">
							<Dropdown
								styleVersion="v2"
								value={formikBag.values[schemeField]}
								onValueChange={value => {
									formikBag.setFieldValue(schemeField, value);
								}}
								options={schemeOptions}
								optionValue={'value'}
								placeholder="Scheme"
								position="top"
							/>
							<FormFieldError field={schemeField} />
						</div>
					</p>
					<p className="control column is-3">
						<div className="field">
							<Dropdown
								styleVersion="v2"
								value={formikBag.values[currencyField]}
								onValueChange={value => {
									formikBag.setFieldValue(currencyField, value);
								}}
								options={currencyMap}
								optionValue={'value'}
								placeholder="Currency"
								position="top"
							/>
							<FormFieldError field={currencyField} />
						</div>
					</p>
					<p className="control column">
						<FormField
							className="has-text-left"
							styleVersion="v2"
							field={amountField}
							type="number"
							placeholder="Amount"
						/>
					</p>
				</div>
			</div>
		);
	}

	renderCompensationFields(formikBag, currencyMap) {
		if (!this.state.loaded) {
			return <Loader />;
		}

		return (
			<div className="compensations columns wizard-row">
				<div className="column is-6">
					{this.renderCompensationSection(formikBag, currencyMap, {
						title: (
							<Fragment>
								Current <strong>Compensation</strong>
							</Fragment>
						),
						currencyField: 'currentCurrencyId',
						amountField: 'currentAmount',
						schemeField: 'currentScheme'
					})}
				</div>
				<div className="column is-6">
					{this.renderCompensationSection(formikBag, currencyMap, {
						title: (
							<Fragment>
								Expected <strong>Compensation</strong>
							</Fragment>
						),
						currencyField: 'expectedCurrencyId',
						amountField: 'expectedAmount',
						schemeField: 'expectedScheme'
					})}
				</div>
			</div>
		);
	}

	getLatestCompensationByType(type) {
		const ledgers = this.props.candidate.candidateCompensationLedgers || [];
		return ledgers.find(entry => entry.type === type);
	}

	renderCompensationDisplay(label, compensation) {
		const display = compensation
			? `${this.findCurrencyObject(compensation.currencyId)?.code || ''} ${compensation.amount}${compensation.scheme === 'hourly' ? '/Hr' : '/Mo'}`
			: '-';

		return (
			<div className="has-text-left">
				<label className="title is-5 has-text-weight-bold">{label}</label>
				<div className="columns">
					<p className="control column">{display}</p>
				</div>
			</div>
		);
	}

	renderCompensations() {
		const latestCurrent = this.getLatestCompensationByType('current');
		const latestExpected = this.getLatestCompensationByType('expected');

		return (
			<div className="compensations columns wizard-row">
				<div className="column is-5">
					{this.renderCompensationDisplay(
						'Current Compensation',
						latestCurrent
					)}
				</div>
				<div className="column is-5">
					{this.renderCompensationDisplay(
						'Expected Compensation',
						latestExpected
					)}
				</div>
				<div className="column">
					<Button
						onClick={() => {
							modalStore.openModal(
								{ title: 'Add Compensation' },
								<CandidateCompensationCreate
									isCandidate
									candidate={this.props.candidate}
									handleSuccess={() => {
										candidateProfileStore.reloadCandidate();
									}}
									handleSave={values =>
										candidatesStore.createCompensation({
											data: values
										})
									}
								/>
							);
						}}
						type="submit"
						className="button is-primary is-big is-rounded"
						label="Update Compensation"
					/>
				</div>
			</div>
		);
	}

	render() {
		return (
			<Formik
				initialValues={this.getInitialValues()}
				validationSchema={ValidationSchema}
				isInitialValid={this.isInitialValid}
				validateOnChange
				validate={values => {}}
				onSubmit={values => {
					this.saving();
					this.save(values);
				}}>
				{formikBag => this.renderForm(formikBag)}
			</Formik>
		);
	}

	renderForm(formikBag) {
		const currencyMap = [
			{ text: 'Currency', value: '' },
			...this.state.currencies.map(currency => ({
				text: `${currency.code} ${currency.symbol}`,
				value: currency.id
			}))
		];

		return (
			<Fragment>
				<div className="expectations-edit form-content maxWidth">
					<section className="hero">
						<div className="hero-body">
							<div className="container has-text-centered">
								<h1 className="title is-2 title-edit">Work Preferences</h1>
							</div>
						</div>
					</section>
					<section className="section">
						<div className="container">
							<div className="columns wizard-row">
								<div className="column is-full">
									<div className="field has-text-left">
										<label className="title is-6 ">
											What type of <strong>roles</strong> are you interested in?{' '}
											<FieldRequiredIndicator />
										</label>
										<DropdownField
											component={JobRoleDropdown}
											name="preferredJobRoles"
											styleVersion="v2"
										/>
										<FormFieldError field="preferredJobRoles" />
									</div>
								</div>
							</div>
							<div className="columns wizard-row">
								<div className="column is-full">
									<div className="field has-text-left">
										<label className="title is-6">
											What are the <strong>technologies</strong> you would like
											to work with? <FieldRequiredIndicator />
										</label>
										<DropdownField
											component={SkillDropdown}
											name="preferredSkills"
											placeholder="You can choose more than one option"
											styleVersion="v2"
										/>
										<FormFieldError field="preferredSkills" />
									</div>
								</div>
							</div>
							<div className="columns wizard-row">
								<div className="column is-full">
									<WorkLocationInput
										value={formikBag.values.preferredWorkLocation}
										onValueChange={value => {
											formikBag.setFieldValue('preferredWorkLocation', value);
											formikBag.setFieldTouched(
												'preferredWorkLocation',
												true,
												false
											);
										}}
										required
									/>
									<FormFieldError field="preferredWorkLocation" />
								</div>
							</div>
							<div className="columns wizard-row">
								<div className="column is-full">
									<div className="field has-text-left">
										<label className="title is-6">
											What type of <strong>positions</strong> are you interested
											in? <FieldRequiredIndicator />
										</label>
										<DropdownField
											component={MultiDropdown}
											name="preferredPositionType"
											placeholder="You can choose more than one option"
											styleVersion="v2"
											options={candidatesStore.preferredPositionTypeOptions}
										/>
										<FormFieldError field="preferredPositionType" />
									</div>
								</div>
							</div>
							<div className="columns wizard-row">
								<div className="column is-8">
									<div className=" has-text-left">
										<label className="title is-6">
											Which <strong>workshifts</strong> would you be willing to
											work in? <FieldRequiredIndicator />
										</label>
										<DropdownField
											component={MultiDropdown}
											name="preferredWorkshifts"
											styleVersion="v2"
											options={candidatesStore.preferredWorkshiftsOptions}
											onValueChange={values => {
												const sortedWorkshifts = values
													? values.sort(this.orderWorkshift)
													: values;
												formikBag.setFieldValue(
													'preferredWorkshifts',
													sortedWorkshifts
												);
												formikBag.setFieldTouched(
													'preferredWorkshifts',
													true,
													false
												);
											}}
										/>
										<FormFieldError field="preferredWorkshifts" />
									</div>
								</div>
								<div className="column is-4">
									<div className=" has-text-left">
										<label className="title is-5 has-text-weight-bold">
											What is your <strong>time zone</strong>?{' '}
											<FieldRequiredIndicator />
										</label>
										<DropdownField
											component={TimezoneDropdown}
											name="preferredTimeZone"
											styleVersion="v2"
										/>
										<FormFieldError field="preferredTimeZone" />
									</div>
								</div>
							</div>
							{this.props.isWizard ? (
								<div className="columns wizard-row">
									<div className="column is-fullwidth">
										<ConditionalContent
											entityName="compensations"
											styleVersion="v2"
											noSelectDefaultValue={0}
											shouldShowContent={this.areCompensationsFilled(formikBag)}
											label={
												<>
													Would you like to enter your{' '}
													<strong>compensation</strong> details?
												</>
											}
											required
											onShow={() => {
												formikBag.setFieldValue(
													'showingCompensationFields',
													true
												);
											}}
											onHide={() => {
												formikBag.setFieldValue(
													'showingCompensationFields',
													false
												);
												this.clearCompensationFields(formikBag);
											}}
											content={() =>
												this.renderCompensationFields(formikBag, currencyMap)
											}
										/>
									</div>
								</div>
							) : (
								this.renderCompensations()
							)}
						</div>
					</section>
					<footer className="footer">
						<div className="container">
							<div className="content has-text-centered">
								{this.props.isWizard ? (
									<div>
										<Button
											onClick={() => {
												formikBag.submitForm();
											}}
											type="submit"
											className="button primary-v2 is-large"
											label={'Continue'}
											loading={this.state.saving}
										/>
									</div>
								) : (
									<Button
										onClick={() => {
											formikBag.submitForm();
										}}
										type="submit"
										className="button primary-v2 is-large"
										label={'Save'}
										loading={this.state.saving}
									/>
								)}
							</div>
						</div>
					</footer>
				</div>
			</Fragment>
		);
	}

	getInitialValues() {
		const preferredPositionType = (
			this.props.candidate.preferredPositionType || []
		).map(item => {
			return {
				text: this.getValueFromOptions(
					item,
					candidatesStore.preferredPositionTypeOptions
				),
				value: item
			};
		});
		const preferredWorkshifts = (
			this.props.candidate.preferredWorkshifts || []
		).map(item => {
			return {
				text: this.getValueFromOptions(
					item,
					candidatesStore.preferredWorkshiftsOptions
				),
				value: item
			};
		});
		const candidateTimeZone = this.props.candidate.timezone
			? this.props.candidate.timezone
			: null;
		const preferredSkills = (this.props.candidate.preferredSkills || []).map(
			item => {
				return item.skill;
			}
		);
		const preferredJobRoles = (
			this.props.candidate.preferredJobRoles || []
		).map(item => {
			return { ...item.jobRole };
		});

		const ledgers = this.props.candidate.candidateCompensationLedgers || [];
		const latestCurrent = ledgers.find(e => e.type === 'current') || {};
		const latestExpected = ledgers.find(e => e.type === 'expected') || {};

		return {
			preferredJobRoles: preferredJobRoles,
			preferredWorkLocation: this.props.candidate.preferredWorkLocation,
			preferredSkills: preferredSkills,
			lookingForJob: this.props.candidate.lookingForJob,
			preferredTimeZone: candidateTimeZone,
			preferredPositionType,
			preferredWorkshifts,
			currentCurrencyId: latestCurrent.currencyId || '',
			currentAmount: latestCurrent.amount || '',
			currentScheme: latestCurrent.scheme || '',
			expectedCurrencyId: latestExpected.currencyId || '',
			expectedAmount: latestExpected.amount || '',
			expectedScheme: latestExpected.scheme || ''
		};
	}

	saving(status) {
		if (status === false || status === true) {
			this.setState({ saving: status });
		} else {
			this.setState({ saving: true });
		}
	}

	buildCompensations(values) {
		const compensations = [];

		if (
			values.currentCurrencyId &&
			values.currentAmount &&
			values.currentScheme
		) {
			compensations.push({
				type: 'current',
				currencyId: values.currentCurrencyId,
				amount: values.currentAmount,
				scheme: values.currentScheme
			});
		}

		if (
			values.expectedCurrencyId &&
			values.expectedAmount &&
			values.expectedScheme
		) {
			compensations.push({
				type: 'expected',
				currencyId: values.expectedCurrencyId,
				amount: values.expectedAmount,
				scheme: values.expectedScheme
			});
		}

		return compensations;
	}

	async save(values) {
		const compensations = this.buildCompensations(values);

		const valuesToSave = {
			candidate: {
				...values,
				preferredPositionType: values.preferredPositionType.map(
					item => item.value
				),
				preferredWorkshifts: values.preferredWorkshifts.map(item => item.value),
				preferredTimeZone: values.preferredTimeZone.id
			},
			preferredJobRoles: values.preferredJobRoles,
			preferredSkills: values.preferredSkills,
			...(this.props.isWizard && this.hasCompensationsChanged(compensations)
				? { compensations }
				: {})
		};
		candidatesStore.updateExpectations(valuesToSave).then(result => {
			this.setState({ saving: false });
			if (result.status === 'success') {
				notificationStore.pushNotification({
					type: notificationStore.SUCCESS_NOTIFICATION,
					title: 'Work preferences saved',
					message: 'Your work preferences were saved successfully.'
				});
				this.props.handleSuccess();
			} else {
				notificationStore.pushNotification({
					type: notificationStore.ERROR_NOTIFICATION,
					title: 'Work preferences error',
					message: 'An error has occurred. Please try again.'
				});
			}
		});
	}

	orderWorkshift(workshiftA, workshiftB) {
		if (workshiftA === 'anytime') {
			return 1;
		}
		const shiftListA = workshiftA.value.split('-');
		const shiftStartsA = shiftListA[0];
		const militaryShiftTimeA = parseInt(
			moment(shiftStartsA, 'ha').format('HH')
		);
		const shiftListB = workshiftB.value.split('-');
		const shiftStartsB = shiftListB[0];
		const militaryShiftTimeB = parseInt(
			moment(shiftStartsB, 'ha').format('HH')
		);
		return militaryShiftTimeA > militaryShiftTimeB ? 1 : -1;
	}

	areCompensationsFilled(formikBag) {
		return (
			!!formikBag.values.currentCurrencyId &&
			!!formikBag.values.currentAmount &&
			!!formikBag.values.currentScheme &&
			!!formikBag.values.expectedCurrencyId &&
			!!formikBag.values.expectedAmount &&
			!!formikBag.values.expectedScheme
		);
	}

	clearCompensationFields(formikBag) {
		formikBag.setFieldValue('currentCurrencyId', '');
		formikBag.setFieldValue('currentAmount', '');
		formikBag.setFieldValue('currentScheme', '');
		formikBag.setFieldValue('expectedCurrencyId', '');
		formikBag.setFieldValue('expectedAmount', '');
		formikBag.setFieldValue('expectedScheme', '');
	}

	hasCompensationsChanged(compensations) {
		if (!compensations || compensations.length === 0) {
			return false;
		}

		const ledgers = this.props.candidate.candidateCompensationLedgers || [];

		return compensations.some(comp => {
			const existing = ledgers.find(e => e.type === comp.type);
			if (!existing) return true;
			return (
				existing.currencyId !== comp.currencyId ||
				Number(existing.amount) !== Number(comp.amount) ||
				existing.scheme !== comp.scheme
			);
		});
	}
}
